import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import { GetContextMenuItemsParams, MenuItemDef, RowNode } from '@ag-grid-enterprise/all-modules'
import { BaseCrud } from '@/shared/models/cruds.models'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import {
    columnsDefNotificationTemplatesGrid,
    ContentCustomNotificationTextTemplateGrid,
    initialNotificationsTemplatesGridState,
    NotificationsTemplatesGridState
} from '@/modules/configurationModule/detail/notifications/components/notification-templates-grid/notifications-templates-grid.model'
import { notificationTemplatesService } from '@/modules/configurationModule/detail/notifications/services/notification-templates-service'
import {
    customNotificationTextTemplatePatchInBatchRequest,
    NotificationTemplateCreateRequest,
    NotificationTemplateUpdateRequest
} from '@/modules/configurationModule/detail/notifications/models/notification-templates'
import NotificationTemplatesModalComponent
    from '@/modules/configurationModule/detail/notifications/components/notification-templates-modal/notification-templates-modal.vue'
import NotificationTemplatesModal
    from '@/modules/configurationModule/detail/notifications/components/notification-templates-modal/notification-templates-modal'
import {
    NotificationTemplatesModalState,
    NotificationTemplatesModalType
} from '@/modules/configurationModule/detail/notifications/components/notification-templates-modal/notification-templates-modal.model'
import { EntityIdentifiersRequest } from '@/shared/models/http/http.model'
import { StrictNullable } from '@/shared/types/app.types'
import { customNotificationTypeService } from '@/modules/configurationModule/detail/notifications/services/custom-notification-type.service'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        NotificationTemplatesModalComponent
    }
})
export default class NotificationTemplatesGridComponent extends BaseCrudServerSideComponent<ContentCustomNotificationTextTemplateGrid> implements BaseCrud {
    @Ref() readonly notificationTemplatesModalWrapper!: any
    @Ref() readonly notificationTemplatesModal!: NotificationTemplatesModal

    state: NotificationsTemplatesGridState
    constructor() {
        super()
        this.state = initialNotificationsTemplatesGridState()
    }

    private async _getCustomNotificationTypes(): Promise<NotificationTemplatesModalType[]> {
        const response =
            await this.$_toCallSecure(
                customNotificationTypeService.getCustomNotificationTypes())
        return response ?? []
    }

    async mounted() {
        const customNotificationTypes = await this._getCustomNotificationTypes()
        this.state.columnsDef = columnsDefNotificationTemplatesGrid(customNotificationTypes)
    }

    get laboratoryId(): number {
        return this.state.laboratoryId
    }

    get isUpdateButtonDisabled(): boolean {
        return this.state.gridApi?.getSelectedRows().length !== 1
    }

    get isDeleteButtonDisabled(): boolean {
        return !this.state.gridApi?.getSelectedRows().length
    }

    getContextMenuItems(params:GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$t('configuration.actions.update').toString(),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => {
                    const nodeSelected:StrictNullable<RowNode> = params?.node
                    const selectedRow:ContentCustomNotificationTextTemplateGrid = nodeSelected?.data
                    return this.showModal(this.$t('configuration.actions.update') as string, selectedRow, this.notificationTemplatesModalWrapper)
                }
            },
            {
                name: this.$t('configuration.actions.delete').toString(),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: () => this.deleteOperation(this.state.selectedRows)
            },
            {
                name: params?.node?.data.active ? this.$t('configuration.actions.deactivate').toString() : this.$t('configuration.actions.activate').toString(),
                cssClasses: this.state.selectedRows.length ? ['not-show'] : [''],
                action: () => this.activateDeactivateSelection(!params?.node?.data?.active, [params?.node?.data])
            },
            {
                name: this.$t('configuration.actions.activate').toString(),
                cssClasses: this.canActivateBatch ? [''] : ['not-show'],
                action: () => this.activateDeactivateSelection(true, this.state.selectedRows)
            },
            {
                name: this.$t('configuration.actions.deactivate').toString(),
                cssClasses: this.canDeactivateBatch ? [''] : ['not-show'],
                action: () => this.activateDeactivateSelection(false, this.state.selectedRows)
            }
        ]
    }
    async activateDeactivateSelection(option: boolean, selectionBatch: ContentCustomNotificationTextTemplateGrid[]): Promise<void> {
        const notificationTemplatesToPatch: customNotificationTextTemplatePatchInBatchRequest[] = selectionBatch
            ?.filter((notificationTemplate: ContentCustomNotificationTextTemplateGrid) => notificationTemplate.active !== option)
            ?.map((notificationTemplate:ContentCustomNotificationTextTemplateGrid) => {
                return {
                    active: option,
                    id: notificationTemplate.id,
                    text: notificationTemplate.text,
                    title: notificationTemplate.title
                }
            })
        if (notificationTemplatesToPatch.length) {
            this.$_updateLoading(true)
            const [err] = await this.$_toCall(notificationTemplatesService.patchTemplateNotificationBatch(notificationTemplatesToPatch, this.laboratoryId), true)
            this.$_updateLoading(false)
            if (err) {
                return
            }
            if (notificationTemplatesToPatch.length > 1) {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 2, { elements: notificationTemplatesToPatch.length }))
            } else {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
            }
            this.reloadData()
        }
    }

    async createOperation(data: NotificationTemplatesModalState): Promise<void> {
        const notificationTemplatesToCreate: NotificationTemplateCreateRequest = {
            active: data.active,
            text: data.text,
            title: data.title,
            type: data.type?.code
        }
        this.$_updateLoading(true)
        const [err] = await this.$_toCall(notificationTemplatesService.createTemplateNotification(notificationTemplatesToCreate, this.laboratoryId), true)
        this.$_updateLoading(false)
        if (err) {
            return
        }
        this.$_showToastSuccess(this.$t('configuration.toastMessages.created')?.toString())
        this.notificationTemplatesModalWrapper.hide()
        this.reloadData()
    }

    async patchOperation(data: NotificationTemplatesModalState): Promise<void> {
        const notificationTemplatesToPatch: NotificationTemplateUpdateRequest = {
            id: data.id,
            active: data.active,
            text: data.text,
            title: data.title
        }
        this.$_updateLoading(true)
        const [err] = await this.$_toCall(notificationTemplatesService.patchTemplateNotificationBatch([notificationTemplatesToPatch], this.laboratoryId), true)
        this.$_updateLoading(false)
        if (err) {
            return
        }
        this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
        this.notificationTemplatesModalWrapper.hide()
        this.reloadData()
    }

    async deleteBatchOperation(notificationTemplates: ContentCustomNotificationTextTemplateGrid[]): Promise<void> {
        const notificationTemplatesToDelete: EntityIdentifiersRequest = {
            entityIdentifiers: notificationTemplates.map((notificationTemplates: ContentCustomNotificationTextTemplateGrid) => Number(notificationTemplates?.id))
        }
        this.$_updateLoading(true)
        const [err] = await this.$_toCall(notificationTemplatesService.deleteTemplateNotificationBatch(notificationTemplatesToDelete, this.laboratoryId), true)
        this.$_updateLoading(false)
        if (err) {
            return
        }
        if (notificationTemplates.length > 1) {
            this.$_showToastSuccess(
                this.$tc('configuration.toastMessages.deleted', 2, { elements: notificationTemplates?.length })
            )
        } else {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', 1))
        }
        this.reloadData()
    }

    async deleteOperation(notificationTemplates: ContentCustomNotificationTextTemplateGrid[]): Promise<void> {
        if (notificationTemplates.length) {
            const selectedRowsSize = this.state?.selectedRows?.length
            const labelConfirmation = this.$tc('configuration.grid.deleteConfirmation')
            const labelGrid = this.$tc('configuration.grid.element', selectedRowsSize)
            const labelDeleteQuestion = this.$tc('configuration.grid.deleteQuestion')
            const labelDeleteAction = this.$tc('configuration.actions.delete')
            const deleteConfirmation = await this.$_showConfirmationModal(
                `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelDeleteQuestion}`,
                labelDeleteAction
            )
            if (deleteConfirmation) {
                await this.deleteBatchOperation(notificationTemplates)
            }
        }
    }

    onUpdateButtonClick(): void {
        const first = this.state.selectedRows[0]
        this.showModal(this.$tc('configuration.actions.update'), first, this.notificationTemplatesModalWrapper)
    }

    async onDeleteButtonClick(): Promise<void> {
        await this.deleteOperation(this.state.selectedRows)
    }

    onAddButtonClick(): void {
        this.showModal(this.$tc('configuration.actions.add'), null, this.notificationTemplatesModalWrapper)
    }

    async onActivateButtonClick(): Promise<void> {
        await this.activateDeactivateSelection(true, this.state.selectedRows)
    }

    async onDeActivateButtonClick(): Promise<void> {
        await this.activateDeactivateSelection(false, this.state.selectedRows)
    }
}
